
import React, { useEffect } from 'react';
import { Header } from "@/components/Header";
import { FooterSection } from "@/components/FooterSection";
import { useScrollToTop } from "@/hooks/useScrollToTop";

const PrivacyPolicy = () => {
  useScrollToTop();

  useEffect(() => {
    document.title = "Privacy Policy | DocketLabs";
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'DocketLabs privacy policy. Learn how we collect, use & protect your personal data. Your privacy rights & contact information.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'DocketLabs privacy policy. Learn how we collect, use & protect your personal data. Your privacy rights & contact information.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);

  return (
    <>
      <Header />
      <div className="pt-24 bg-background min-h-screen">
        <div className="container mx-auto px-6 py-16 max-w-4xl">
          <div className="prose prose-invert max-w-none">
            <h1 className="text-4xl font-bold mb-8">DocketLabs Privacy Policy</h1>
            <p className="text-lg text-white/70 mb-8">Last Updated: May 24th, 2025</p>
            
            <p className="mb-6">
              DocketLabs, LLC ("DocketLabs," "we," "us," or "our") is committed to respecting your privacy and protecting your personal data. This Privacy Policy explains how we collect, use, share, and protect your personal information when you interact with our website, subscribe to our marketing emails, or use our AI consultancy and training services for law firms.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Scope of This Policy</h2>
            <p className="mb-6">
              This Policy applies to personal data of our clients, prospective clients, website visitors, business contacts, and individuals who interact with DocketLabs in relation to our services. It does not cover any data we process solely on behalf of clients under service agreements.
            </p>
            <p className="mb-6">
              By using our website or services, you acknowledge that you have read and understand this Policy.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Who We Are</h2>
            <p className="mb-6">
              DocketLabs is a Limited Liability Company registered in the state of Florida, USA, providing AI consultancy and training services to law firms. We operate as a single entity with no subsidiaries, affiliates, or parent companies.
            </p>

            <h3 className="text-xl font-semibold mt-6 mb-3">Contact Information</h3>
            <div className="mb-6">
              <p><strong>Email:</strong> hello@docketlabs.com</p>
              <p><strong>Mail:</strong></p>
              <div className="ml-4">
                <p>DocketLabs, LLC</p>
                <p>10552 Cypress Lakes Preserve Dr.</p>
                <p>Lake Worth, FL 33449</p>
                <p>(Please label mail: Attention: DocketLabs, LLC)</p>
              </div>
            </div>

            <h2 className="text-2xl font-semibold mt-8 mb-4">What Personal Data We Collect</h2>
            <p className="mb-4">We collect and process the following personal data:</p>
            <ul className="list-disc list-inside mb-6 space-y-2">
              <li><strong>Basic Contact & Professional Information:</strong> Name, work email, phone number, business address, company name, and role.</li>
              <li><strong>IP Addresses & Website Usage Data:</strong> Collected automatically when you visit our website.</li>
              <li><strong>Publicly Available Professional Profiles:</strong> If relevant to our outreach or business development.</li>
            </ul>
            <p className="mb-6">
              We do not knowingly collect sensitive personal data or data about children under 16 years of age.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">How We Collect Your Data</h2>
            <ul className="list-disc list-inside mb-6 space-y-2">
              <li>Directly from you when you fill out forms, contact us, or opt in to marketing communications.</li>
              <li>Automatically through cookies and similar technologies when you visit our website.</li>
              <li>From publicly available sources for business-to-business purposes.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">How We Use Your Personal Data</h2>
            <p className="mb-4">We use your personal data to:</p>
            <ul className="list-disc list-inside mb-6 space-y-2">
              <li>Provide, improve, and tailor our AI consultancy and training services.</li>
              <li>Communicate with you and respond to inquiries.</li>
              <li>Send marketing emails and newsletters only on an opt-in basis (you can opt out anytime).</li>
              <li>Improve our website and understand usage patterns.</li>
              <li>Fulfill legal or contractual obligations and protect our rights.</li>
            </ul>
            <p className="mb-6">
              We do not sell your personal data.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Legal Basis and Jurisdiction</h2>
            <p className="mb-6">
              We operate under U.S. law and the jurisdiction of the state of Florida. For international clients, we process data based on U.S. jurisdiction unless otherwise specified in a client agreement.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Sharing of Your Personal Data</h2>
            <p className="mb-4">We may share personal data only as needed for business purposes:</p>
            <ul className="list-disc list-inside mb-6 space-y-2">
              <li>With trusted service providers for IT, hosting, analytics, or communication services.</li>
              <li>As required by law or to protect our legal rights.</li>
              <li>With client law firms in the context of a client engagement, always under strict confidentiality.</li>
              <li>In the event of a business transfer, where permitted by law.</li>
            </ul>
            <p className="mb-6">
              We ensure appropriate safeguards for any international data transfers and will disclose these in client agreements.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Cookies and Tracking</h2>
            <p className="mb-6">
              We use essential cookies for website functionality and may use additional cookies for analytics and advertising. You can manage cookie preferences through your browser settings or the cookie notice on our website.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Data Security and Retention</h2>
            <p className="mb-6">
              We maintain reasonable and appropriate security measures to protect your data. Personal data is kept only as long as necessary for business, legal, or regulatory reasons.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Your Privacy Rights</h2>
            <p className="mb-4">Depending on your location, you may have rights to:</p>
            <ul className="list-disc list-inside mb-6 space-y-2">
              <li>Access your data.</li>
              <li>Correct or update your data.</li>
              <li>Delete your data (subject to legal exceptions).</li>
              <li>Object to or restrict processing.</li>
              <li>Withdraw consent for marketing at any time.</li>
              <li>Lodge a complaint with a data protection authority.</li>
            </ul>

            <h2 className="text-2xl font-semibold mt-8 mb-4">California Privacy Rights (CCPA)</h2>
            <p className="mb-4">If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
            <ul className="list-disc list-inside mb-6 space-y-2">
              <li><strong>Right to Know:</strong> Request information about the categories of personal information we collect, use, and share.</li>
              <li><strong>Right to Access:</strong> Request access to the specific pieces of personal information we have collected about you.</li>
              <li><strong>Right to Deletion:</strong> Request deletion of your personal information (subject to legal exceptions).</li>
              <li><strong>Right to Opt Out of Sale:</strong> We do not sell personal data, so this right does not apply.</li>
              <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of these rights.</li>
            </ul>
            <p className="mb-6">
              You may submit a request to exercise your California privacy rights by emailing hello@docketlabs.com. We may ask for proof of identity to verify your request and will respond within the timeframe required by law (generally within 45 days for California residents).
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Confidential and Client-Specific Data</h2>
            <p className="mb-6">
              Any confidential or sensitive data shared with us by a client will be used only for that client's purposes, under strict confidentiality, and according to any applicable NDAs or contracts.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">Updates to This Policy</h2>
            <p className="mb-6">
              We may update this Privacy Policy from time to time. The "Last Updated" date at the top indicates the latest version. Significant changes will be communicated via our website or directly if required.
            </p>

            <h2 className="text-2xl font-semibold mt-8 mb-4">No Sale of Personal Data</h2>
            <p className="mb-6">
              DocketLabs does not sell personal data collected from the website or during the course of business with clients.
            </p>
          </div>
        </div>
      </div>
      <FooterSection />
    </>
  );
};

export default PrivacyPolicy;
